import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

/**
 * Renders a server-supplied date-time string in the browser's local timezone.
 *
 * The backend serializes UTC `DateTime` values as naive ISO strings (e.g.
 * `"2026-05-26T10:59:00"` — no `Z`, no offset). JavaScript's `new Date(...)`
 * would interpret that as the user's local time and show the UTC wall-clock
 * verbatim (10:59 instead of 12:59 Oslo). This pipe normalizes the input
 * to UTC first, then delegates to Angular's built-in `DatePipe` so the
 * configured format (`dd-MM-yyyy HH:mm`, etc.) is rendered in the user's
 * own zone.
 *
 * Usage: `{{ meeting.CompletedDate | utcDate : 'dd-MM-yyyy HH:mm' }}`
 *
 * The pipe is a no-op for inputs that already carry a `Z` or `±HH:mm`
 * offset (so it's safe to apply everywhere without auditing each field).
 */
@Pipe({ name: 'utcDate',
  standalone: false })
export class UtcDatePipe implements PipeTransform {
  // Matches the trailing timezone marker in a well-formed ISO 8601 string.
  // We treat anything that already has Z or an explicit offset as
  // unambiguous and skip the normalization.
  private static readonly timezoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/i;

  private readonly datePipe = new DatePipe('en-US');

  transform(value: string | Date | null | undefined, format: string = 'mediumDate'): string | null {
    if (value == null) {
      return null;
    }

    let normalized: string | Date = value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        return null;
      }
      normalized = UtcDatePipe.timezoneSuffix.test(trimmed) ? trimmed : `${trimmed}Z`;
    }

    return this.datePipe.transform(normalized, format);
  }
}
